import React, { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Building2,
  Home,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
} from 'lucide-react'
import { Label } from '../ui/Label'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import type { IdentifiedProperty } from '../identification/IdentificationPropertyRow'
import {
  reconcileFinalizedAllocations,
  validatePurchaseClose,
  type IdentificationRule,
} from '../../lib/identification/ruleEnforcement'

export interface PropertyFinalizationState {
  acquired: boolean
  finalAmount: number
  confirmed: boolean
}

export interface PurchaseFinalizationListProps {
  identifiedProperties: IdentifiedProperty[]
  finalizationStates: PropertyFinalizationState[]
  idRule: IdentificationRule | ''
  disabled: boolean
  onStatesChange: (states: PropertyFinalizationState[]) => void
}

function formatUSD(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

const TYPE_BADGE_CLASSES: Record<'dst' | 'like-kind', string> = {
  dst: 'inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 text-[10px] font-semibold',
  'like-kind':
    'inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 text-[10px] font-semibold',
}

const TYPE_BADGE_LABELS: Record<'dst' | 'like-kind', string> = {
  dst: 'DST',
  'like-kind': 'Like-Kind',
}

const ID_RULE_LABELS: Record<IdentificationRule, string> = {
  '3-property': '3-property rule',
  '200-percent': '200% rule',
  '95-percent': '95% rule',
}

const RECONCILIATION_STATUS_STYLES: Record<
  'balanced' | 'over' | 'under' | 'empty',
  { bar: string; text: string; icon: React.ElementType; label: string }
> = {
  balanced: {
    bar: 'border-green-200 dark:border-green-800 bg-green-50/60 dark:bg-green-900/10',
    text: 'text-green-700 dark:text-green-300',
    icon: CheckCircle2,
    label: 'Matches your identification',
  },
  over: {
    bar: 'border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10',
    text: 'text-amber-700 dark:text-amber-300',
    icon: TrendingUp,
    label: 'Over by',
  },
  under: {
    bar: 'border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-900/10',
    text: 'text-rose-700 dark:text-rose-300',
    icon: TrendingDown,
    label: 'Under by',
  },
  empty: {
    bar: 'border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/30',
    text: 'text-slate-500 dark:text-slate-400',
    icon: Minus,
    label: 'Enter final acquired amounts below',
  },
}

const finalAmountSchema = z.object({
  finalAmount: z.string().min(1, 'Amount is required'),
})

type FinalAmountValues = z.infer<typeof finalAmountSchema>

interface PropertyCardProps {
  property: IdentifiedProperty
  index: number
  state: PropertyFinalizationState
  disabled: boolean
  onChange: (state: PropertyFinalizationState) => void
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  index,
  state,
  disabled,
  onChange,
}) => {
  const [expanded, setExpanded] = useState(!state.confirmed)
  const idHubAmount =
    property.investmentAmount ?? property.allocationAmount ?? property.estimatedFMV

  const { register, handleSubmit, formState: { errors }, watch } = useForm<FinalAmountValues>({
    resolver: zodResolver(finalAmountSchema),
    defaultValues: { finalAmount: state.finalAmount > 0 ? String(state.finalAmount) : '' },
  })

  const currentRaw = watch('finalAmount')
  const currentAmount = parseFloat(currentRaw) || 0

  const onConfirm = useCallback(
    (values: FinalAmountValues) => {
      const amount = parseFloat(values.finalAmount) || 0
      onChange({ acquired: true, finalAmount: amount, confirmed: true })
      setExpanded(false)
    },
    [onChange]
  )

  const handleUnconfirm = useCallback(() => {
    onChange({ ...state, confirmed: false })
    setExpanded(true)
  }, [state, onChange])

  const handleToggleAcquired = useCallback(() => {
    if (state.acquired && !state.confirmed) {
      onChange({ acquired: false, finalAmount: 0, confirmed: false })
    } else if (!state.acquired) {
      onChange({ acquired: true, finalAmount: state.finalAmount, confirmed: false })
      setExpanded(true)
    }
  }, [state, onChange])

  const isDst = property.type === 'dst'
  const Icon = isDst ? Building2 : Home
  const displayTitle = property.dstTitle ?? property.address
  const portfolioCount = property.properties?.length ?? 0

  return (
    <div
      className={[
        'rounded-xl border transition-all',
        state.confirmed
          ? 'border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-900/10'
          : state.acquired
          ? 'border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800/60'
          : 'border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/20',
      ].join(' ')}
      aria-label={`Property ${index + 1}: ${displayTitle}`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <span
          className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center text-[11px] font-bold mt-0.5"
          aria-hidden="true"
        >
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <Icon className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" aria-hidden="true" />
            <span className={TYPE_BADGE_CLASSES[property.type]}>
              {TYPE_BADGE_LABELS[property.type]}
            </span>
            {isDst && portfolioCount > 1 && (
              <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold">
                Portfolio · {portfolioCount}
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate leading-snug">
            {displayTitle}
          </p>
          {isDst && property.address !== displayTitle && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">
              {property.address}
            </p>
          )}
          <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-1">
            ID Hub allocation: {idHubAmount > 0 ? formatUSD(idHubAmount) : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {state.confirmed && (
            <span
              className="text-[10px] font-semibold text-green-600 dark:text-green-400 flex items-center gap-1"
              aria-label="Acquired and confirmed"
            >
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Confirmed
            </span>
          )}
          {!state.confirmed && (
            <button
              type="button"
              onClick={handleToggleAcquired}
              disabled={disabled}
              aria-pressed={state.acquired}
              aria-label={state.acquired ? `Mark property ${index + 1} as not acquired` : `Mark property ${index + 1} as acquired`}
              className={[
                'flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors',
                state.acquired
                  ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300'
                  : 'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-purple-300 dark:hover:border-purple-600',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              ].join(' ')}
            >
              {state.acquired
                ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                : <Circle className="h-3.5 w-3.5" aria-hidden="true" />}
              {state.acquired ? 'Acquiring' : 'Not acquiring'}
            </button>
          )}
          {(state.acquired || state.confirmed) && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              aria-controls={`finalization-detail-${index}`}
              aria-label={expanded ? `Collapse property ${index + 1} details` : `Expand property ${index + 1} details`}
              className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              {expanded
                ? <ChevronUp className="h-4 w-4" aria-hidden="true" />
                : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
            </button>
          )}
        </div>
      </div>

      {state.confirmed && !expanded && (
        <div className="px-4 pb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-green-700 dark:text-green-300">
            {formatUSD(state.finalAmount)}
          </p>
          {!disabled && (
            <button
              type="button"
              onClick={handleUnconfirm}
              aria-label={`Edit property ${index + 1} final amount`}
              className="text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium"
            >
              Edit
            </button>
          )}
        </div>
      )}

      {(state.acquired || state.confirmed) && expanded && (
        <div
          id={`finalization-detail-${index}`}
          className="px-4 pb-4 border-t border-purple-100 dark:border-purple-900/30 mt-0 pt-3 space-y-3"
        >
          {state.confirmed ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-700 dark:text-purple-300 font-medium mb-0.5">
                  Final acquired amount
                </p>
                <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                  {formatUSD(state.finalAmount)}
                </p>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={handleUnconfirm}
                  aria-label={`Edit property ${index + 1} final amount`}
                  className="text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium"
                >
                  Edit
                </button>
              )}
            </div>
          ) : (
            <form
              id={`finalization-form-${index}`}
              onSubmit={handleSubmit(onConfirm)}
              noValidate
              className="space-y-3"
            >
              <div className="space-y-1">
                <Label
                  htmlFor={`final-amount-${index}`}
                  className="text-xs text-purple-700 dark:text-purple-300"
                >
                  {isDst ? 'Final DST investment amount' : 'Final acquired amount'}
                </Label>
                <div className="relative flex items-center">
                  <DollarSign
                    className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none"
                    aria-hidden="true"
                  />
                  <Input
                    id={`final-amount-${index}`}
                    type="number"
                    aria-label={isDst ? `Property ${index + 1} final DST investment amount` : `Property ${index + 1} final acquired amount`}
                    placeholder="0"
                    disabled={disabled}
                    {...register('finalAmount')}
                    className="pl-8 border-purple-300 dark:border-purple-600 focus-visible:ring-purple-400"
                  />
                </div>
                {errors.finalAmount && (
                  <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                    {errors.finalAmount.message}
                  </p>
                )}
                {idHubAmount > 0 && currentAmount > 0 && Math.abs(currentAmount - idHubAmount) > 1 && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    ID Hub allocation: {formatUSD(idHubAmount)} · delta{' '}
                    {currentAmount > idHubAmount ? '+' : ''}
                    {formatUSD(currentAmount - idHubAmount)}
                  </p>
                )}
              </div>
              {!disabled && (
                <Button
                  type="submit"
                  form={`finalization-form-${index}`}
                  size="sm"
                  aria-label={`Confirm property ${index + 1} final amount`}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Confirm amount
                </Button>
              )}
            </form>
          )}
        </div>
      )}
    </div>
  )
}

interface ReconciliationBarProps {
  idHubTotal: number
  finalizedTotal: number
  status: 'balanced' | 'over' | 'under' | 'empty'
  delta: number
}

const ReconciliationBar: React.FC<ReconciliationBarProps> = ({
  idHubTotal,
  finalizedTotal,
  status,
  delta,
}) => {
  const style = RECONCILIATION_STATUS_STYLES[status]
  const StatusIcon = style.icon

  return (
    <div
      className={[
        'rounded-xl border px-4 py-3 space-y-2',
        style.bar,
      ].join(' ')}
      aria-label="Allocation reconciliation summary"
      role="status"
    >
      <div className="flex items-center gap-2">
        <StatusIcon className={['h-4 w-4 flex-shrink-0', style.text].join(' ')} aria-hidden="true" />
        <span className={['text-sm font-semibold', style.text].join(' ')}>
          {status === 'balanced' || status === 'empty'
            ? style.label
            : `${style.label} ${formatUSD(Math.abs(delta))}`}
        </span>
      </div>
      {status !== 'empty' && (
        <div className="grid grid-cols-2 gap-3 pt-0.5">
          <div>
            <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              ID Hub total
            </p>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {formatUSD(idHubTotal)}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Finalized total
            </p>
            <p className={['text-sm font-semibold', style.text].join(' ')}>
              {formatUSD(finalizedTotal)}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

interface ComplianceIndicatorProps {
  valid: boolean
  blockReason?: string
  rule: IdentificationRule | ''
}

const ComplianceIndicator: React.FC<ComplianceIndicatorProps> = ({
  valid,
  blockReason,
  rule,
}) => {
  if (!rule) return null

  const ruleLabel: Record<IdentificationRule, string> = {
    '3-property': '3-Property Rule',
    '200-percent': '200% Rule',
    '95-percent': '95% Rule',
  }

  return (
    <div
      className={[
        'rounded-xl border px-4 py-3',
        valid
          ? 'border-green-200 dark:border-green-800 bg-green-50/60 dark:bg-green-900/10'
          : 'border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-900/10',
      ].join(' ')}
      aria-label={valid ? 'IRS compliance check passed' : 'IRS compliance check failed'}
      role="status"
    >
      <div className="flex items-start gap-2">
        {valid
          ? <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          : <ShieldAlert className="h-4 w-4 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" aria-hidden="true" />}
        <div>
          <p className={[
            'text-sm font-semibold',
            valid ? 'text-green-700 dark:text-green-300' : 'text-rose-700 dark:text-rose-300',
          ].join(' ')}>
            {valid
              ? `Within IRS §1031 guidelines — ${rule ? ruleLabel[rule as IdentificationRule] : ''}`
              : 'IRS §1031 compliance issue'}
          </p>
          {!valid && blockReason && (
            <p className="text-xs text-rose-600 dark:text-rose-400 mt-0.5 leading-relaxed">
              {blockReason}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export function PurchaseFinalizationList({
  identifiedProperties,
  finalizationStates,
  idRule,
  disabled,
  onStatesChange,
}: PurchaseFinalizationListProps) {
  const anyConfirmed = finalizationStates.some((s) => s.acquired && s.confirmed)

  const finalizedAmounts = finalizationStates.map((s) =>
    s.acquired && s.confirmed ? s.finalAmount : 0
  )

  const propertiesForRules = identifiedProperties.map((p) => ({
    estimatedFMV: p.estimatedFMV || 0,
    allocationAmount: p.allocationAmount,
  }))

  const rawReconciliation = reconcileFinalizedAllocations(propertiesForRules, finalizedAmounts)
  const reconciliation = !anyConfirmed
    ? { ...rawReconciliation, status: 'empty' as const }
    : rawReconciliation

  const complianceResult =
    anyConfirmed && idRule
      ? validatePurchaseClose(
          idRule as IdentificationRule,
          propertiesForRules,
          reconciliation.finalizedTotal,
        )
      : null

  const reconciliationOk = reconciliation.status === 'balanced'
  const complianceOk = complianceResult ? complianceResult.valid : true

  const allPropertiesHandled = identifiedProperties.length > 0
    && finalizationStates.every((s) => s.confirmed || !s.acquired)
    && finalizationStates.some((s) => s.acquired && s.confirmed)

  const readyToConfirm = allPropertiesHandled && reconciliationOk && complianceOk

  const handlePropertyChange = useCallback(
    (index: number, updated: PropertyFinalizationState) => {
      const next = finalizationStates.map((s, i) => (i === index ? updated : s))
      onStatesChange(next)
    },
    [finalizationStates, onStatesChange]
  )

  if (identifiedProperties.length === 0) {
    return (
      <div
        className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4"
        aria-label="No identified properties to finalize"
      >
        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
          No properties to finalize
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Complete the Identification Hub first.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <Building2
          className="h-4 w-4 text-purple-600 dark:text-purple-400 flex-shrink-0"
          aria-hidden="true"
        />
        <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
          Finalize each identified property
        </h3>
        <span
          className="ml-auto text-xs text-purple-600 dark:text-purple-400 font-medium"
          aria-label={`${identifiedProperties.length} ${identifiedProperties.length === 1 ? 'property' : 'properties'}${idRule ? `, ${ID_RULE_LABELS[idRule as IdentificationRule]}` : ''}`}
        >
          {identifiedProperties.length}{' '}
          {identifiedProperties.length === 1 ? 'property' : 'properties'}
          {idRule && (
            <span className="ml-1 text-purple-400 dark:text-purple-500 font-normal">
              ({ID_RULE_LABELS[idRule as IdentificationRule]})
            </span>
          )}
        </span>
      </div>

      <ul className="space-y-3" aria-label="Property finalization list">
        {identifiedProperties.map((property, index) => (
          <li key={index}>
            <PropertyCard
              property={property}
              index={index}
              state={finalizationStates[index] ?? { acquired: false, finalAmount: 0, confirmed: false }}
              disabled={disabled}
              onChange={(updated) => handlePropertyChange(index, updated)}
            />
          </li>
        ))}
      </ul>

      <ReconciliationBar
        idHubTotal={reconciliation.idHubTotal}
        finalizedTotal={reconciliation.finalizedTotal}
        status={reconciliation.status}
        delta={reconciliation.delta}
      />

      {anyConfirmed && idRule && (
        <ComplianceIndicator
          valid={complianceOk}
          blockReason={complianceResult?.blockReason}
          rule={idRule}
        />
      )}

      {!readyToConfirm && anyConfirmed && (
        <div
          className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 px-4 py-3"
          role="alert"
          aria-label="Cannot confirm property tab yet"
        >
          <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
            {!reconciliationOk
              ? 'Finalized amounts must match the ID Hub allocation total to confirm this tab.'
              : !complianceOk
              ? 'Resolve the IRS compliance issue above before confirming.'
              : 'Confirm all acquired properties before proceeding.'}
          </p>
        </div>
      )}

      {readyToConfirm && (
        <div
          className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50/40 dark:bg-green-900/10 px-4 py-3"
          role="status"
          aria-label="Property finalization complete"
        >
          <p className="text-xs text-green-700 dark:text-green-300 font-semibold">
            All properties finalized — amounts balanced and IRS compliant. You may confirm this tab.
          </p>
        </div>
      )}
    </div>
  )
}

export function buildInitialFinalizationStates(
  count: number,
): PropertyFinalizationState[] {
  return Array.from({ length: count }, () => ({
    acquired: false,
    finalAmount: 0,
    confirmed: false,
  }))
}

export function finalizationReadyToConfirm(
  identifiedProperties: ReadonlyArray<{ estimatedFMV: number; allocationAmount?: number }>,
  states: PropertyFinalizationState[],
  idRule: IdentificationRule | '',
): boolean {
  if (identifiedProperties.length === 0) return false
  const anyAcquiredConfirmed = states.some((s) => s.acquired && s.confirmed)
  if (!anyAcquiredConfirmed) return false
  const allHandled = states.every((s) => s.confirmed || !s.acquired)
  if (!allHandled) return false
  const finalizedAmounts = states.map((s) => (s.acquired && s.confirmed ? s.finalAmount : 0))
  const reconciliation = reconcileFinalizedAllocations(identifiedProperties, finalizedAmounts)
  if (reconciliation.status !== 'balanced') return false
  if (!idRule) return true
  const propertiesForRules = identifiedProperties.map((p) => ({
    estimatedFMV: p.estimatedFMV || 0,
    allocationAmount: p.allocationAmount,
  }))
  const closeResult = validatePurchaseClose(
    idRule as IdentificationRule,
    propertiesForRules,
    reconciliation.finalizedTotal,
  )
  return closeResult.valid
}
